/* eslint-disable react/no-unsafe */
/*
 * The critter that provides encouraging messages, and tells you when you can
 * proceed to the next step or when you need to go back a step.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {staticUrl} from "@khan/urls";

import {
    getNextCompletionMessage,
    getNextProgressMessage,
} from "./scratchpad-challenge.ts";

/* Reasons the critter might appear */
export const CritterAppearance = {
    // You completed the challenge
    CHALLENGE_COMPLETE: "challenge_complete" as const,
    // You did all the challenge steps.
    STEPS_COMPLETE: "steps_complete" as const,
    // You should go to the next step.
    GO_TO_NEXT_STEP: "go_to_next_step" as const,
    // You need to go back to the previous step.
    FIX_PREVIOUS_STEP: "go_back_a_step" as const,
} as const;

const getCritterAppearanceReason = (challenge: any) => {
    if (challenge.previousStepFailed()) {
        return CritterAppearance.FIX_PREVIOUS_STEP;
    }
    if (challenge.currentStepComplete()) {
        return CritterAppearance.GO_TO_NEXT_STEP;
    }
    if (
        // We need to check allTestsPass() to make sure that we don't show
        // CompletionCritter when the learner starts a challenge over
        // and hasn't worked on any steps yet.
        challenge.allTestsPass() &&
        // Challenge will be "complete" if they just completed the challenge,
        // and userScratchpad will be "isComplete" if they completed in past
        (challenge.get("complete") ||
            challenge.get("userScratchpad").get("isComplete"))
    ) {
        return CritterAppearance.CHALLENGE_COMPLETE;
    }
    if (challenge.canBeCompleted()) {
        return CritterAppearance.STEPS_COMPLETE;
    }
    return false;
};

type Props = {
    // Called when the bubble or button button is clicked
    onAcknowledge: () => void;
    // The reason the critter is appearing, dictating what it should do
    // NOTE: "challenge_complete" is omitted here because `ScratchpadToolbar`
    // renders `CompletionCritter` in that case.
    reason: "steps_complete" | "go_to_next_step" | "go_back_a_step";
};

type State = {
    bubbleClosed: boolean;
    completionMessage: any;
    progressMessage: any;
};

class ChallengeCritter extends React.Component<Props, State> {
    static CritterAppearance: any = CritterAppearance;
    static getCritterAppearanceReason: any = getCritterAppearanceReason;

    state: State = {
        bubbleClosed: false,
        completionMessage: getNextCompletionMessage(),
        progressMessage: getNextProgressMessage(),
    };

    UNSAFE_componentWillUpdate(nextProps: any, nextState: any) {
        if (nextProps.reason !== this.props.reason) {
            if (nextProps.reason === CritterAppearance.STEPS_COMPLETE) {
                this.setState({
                    bubbleClosed: false,
                    completionMessage: getNextCompletionMessage(),
                });
            } else if (nextProps.reason === CritterAppearance.GO_TO_NEXT_STEP) {
                this.setState({
                    bubbleClosed: false,
                    progressMessage: getNextProgressMessage(),
                });
            }
        }
    }

    renderCritter: () => React.ReactNode = () => {
        const src =
            this.props.reason === CritterAppearance.STEPS_COMPLETE
                ? critterImages.jumping
                : critterImages.happy;
        return (
            <img
                alt=""
                aria-hidden={true}
                className={css(styles.critter)}
                src={staticUrl(src)}
            />
        );
    };

    renderMessageText: () => React.ReactNode = () => {
        if (this.props.reason === CritterAppearance.STEPS_COMPLETE) {
            return this.state.completionMessage;
        }
        if (this.props.reason === CritterAppearance.GO_TO_NEXT_STEP) {
            return this.state.progressMessage;
        }
        return t`Oops, you need to fix a previous step!`;
    };

    renderMessage: () => React.ReactNode = () => {
        return (
            <div className={css(styles.message)}>
                <span>{this.renderMessageText()}</span>
            </div>
        );
    };

    renderBubble: () => React.ReactNode = () => {
        if (this.state.bubbleClosed) {
            return null;
        }

        return (
            <div>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error */}
                <div
                    className={css(styles.bubble)}
                    onClick={() => {
                        this.setState({bubbleClosed: true});
                        this.props.onAcknowledge();
                    }}
                >
                    <div className={css(styles.inner)}>
                        {this.renderCritter()}
                        {this.renderMessage()}
                    </div>
                </div>
                <div className={css(styles.triangle)} />
            </div>
        );
    };

    render(): React.ReactNode {
        const CritterButtonMessages = {
            [CritterAppearance.STEPS_COMPLETE]: t`Finish challenge`,
            [CritterAppearance.GO_TO_NEXT_STEP]: t`Next step`,
            [CritterAppearance.FIX_PREVIOUS_STEP]: t`Go back`,
        } as const;
        return (
            <div className={css(styles.wrap)}>
                {this.renderBubble()}
                <Button
                    onClick={this.props.onAcknowledge}
                    style={styles.toolbarButton}
                >
                    {CritterButtonMessages[this.props.reason]}
                </Button>
            </div>
        );
    }
}

const critterImages = {
    happy: "/images/scratchpads/happy-critter.png",
    jumping: "/images/scratchpads/jumping-critter.png",
} as const;

const bubbleDistance = 31;
const bubbleHeight = 80;
const triangleSize = 9;
const zIndex = 2; // This oughta do it

// Yeah, yeah, I know.
// TODO(ian): Make white when the editor theme is updated.
const white = "#f5f5f5";

const styles = StyleSheet.create({
    bubble: {
        background: white,
        top: 0 - bubbleDistance - bubbleHeight - triangleSize,
        color: "black",
        borderRadius: 10,
        cursor: "pointer",
        display: "inline-block",
        maxWidth: 250,
        minWidth: 200,
        padding: 10,
        position: "absolute",
        right: 0,
        zIndex: zIndex,
    },
    critter: {
        height: 70,
        marginRight: 10,
    },
    inner: {
        display: "flex",
        flexDirection: "row-reverse",
        justifyContent: "space-around",
    },
    message: {
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "center",
    },
    toolbarButton: {
        marginLeft: 16,
    },
    triangle: {
        borderLeft: `${triangleSize}px solid transparent`,
        borderRight: `${triangleSize}px solid transparent`,
        borderTop: `${triangleSize}px solid ${white}`,
        height: 0,
        left: "calc(50% - 8px)", // 8px is button padding
        position: "absolute",
        top: 0 - bubbleDistance,
        width: 0,
        zIndex: zIndex,
    },
    wrap: {
        display: "inline-block",
        position: "relative",
    },
});

export default ChallengeCritter;
